import type {
  BonusEvent,
  BonusRuntimeSnapshot,
} from "../application/bonus-runtime";
import { bonusVisualConfig } from "../config/bonus-visual-config";
import { motionConfig, type MotionProfile } from "../config/motion-config";

export interface BonusDrawingSurface {
  clear(): this;
  fillCircle(x: number, y: number, radius: number): this;
  fillStyle(color: number, alpha?: number): this;
  fillTriangle(
    x0: number,
    y0: number,
    x1: number,
    y1: number,
    x2: number,
    y2: number,
  ): this;
  lineStyle(lineWidth: number, color: number, alpha?: number): this;
  strokeCircle(x: number, y: number, radius: number): this;
}

export const getBonusHaloRadius = (
  radius: number,
  simulationTimeMs: number,
  motionProfile: MotionProfile,
): number => {
  if (motionProfile.pulseScale === 1) {
    return radius + bonusVisualConfig.haloGap;
  }
  const phase =
    (simulationTimeMs % bonusVisualConfig.haloPulseDurationMs) /
    bonusVisualConfig.haloPulseDurationMs;
  return (
    radius +
    bonusVisualConfig.haloGap +
    Math.sin(phase * Math.PI * 2) * bonusVisualConfig.haloPulseAmount
  );
};

export const getBonusPickupFeedback = (
  event: BonusEvent | null,
  simulationTimeMs: number,
  motionProfile: MotionProfile,
): "Turbo!" | null => {
  if (event === null) {
    return null;
  }
  const duration =
    motionProfile.particleOpacity === 0
      ? bonusVisualConfig.reducedMotionFeedbackDurationMs
      : bonusVisualConfig.feedbackDurationMs;
  const age = simulationTimeMs - event.occurredAtMs;
  if (age < 0 || age >= duration) {
    return null;
  }
  return "Turbo!";
};

const drawTurboIcon = (
  graphics: BonusDrawingSurface,
  x: number,
  y: number,
  radius: number,
): void => {
  graphics.fillTriangle(
    x + radius * 0.12,
    y - radius * 0.72,
    x - radius * 0.46,
    y + radius * 0.08,
    x + radius * 0.08,
    y + radius * 0.08,
  );
  graphics.fillTriangle(
    x - radius * 0.08,
    y - radius * 0.08,
    x + radius * 0.46,
    y - radius * 0.08,
    x - radius * 0.12,
    y + radius * 0.72,
  );
};

export const drawBonuses = (
  graphics: BonusDrawingSurface,
  snapshot: BonusRuntimeSnapshot,
  simulationTimeMs = 0,
  motionProfile: MotionProfile = motionConfig.standard,
): void => {
  graphics.clear();
  for (const bonus of snapshot.bonuses) {
    const style = bonusVisualConfig.styles[bonus.type];
    graphics.lineStyle(3, bonusVisualConfig.haloColor, 0.82);
    graphics.strokeCircle(
      bonus.position.x,
      bonus.position.y,
      getBonusHaloRadius(bonus.radius, simulationTimeMs, motionProfile),
    );
    graphics.fillStyle(style.fillColor, 0.96);
    graphics.fillCircle(bonus.position.x, bonus.position.y, bonus.radius);
    graphics.lineStyle(3, style.outlineColor, 0.92);
    graphics.strokeCircle(bonus.position.x, bonus.position.y, bonus.radius);
    graphics.fillStyle(style.iconColor, 1);
    drawTurboIcon(graphics, bonus.position.x, bonus.position.y, bonus.radius);
  }
};
